import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ApiError, api, keys, unwrap, useDatasets } from '@/api';
import { timeAgo } from '@/lib/format';

import { Button, Card, Empty, Field, Input, Spinner } from './ui';

const MAX_BYTES = 3 * 1024 * 1024;
const kib = (n: number) => (n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KiB` : `${(n / 1024 / 1024).toFixed(1)} MiB`);
/** `Office temp (2026).csv` → `office-temp-2026` */
const nameOf = (file: string) =>
	file
		.replace(/\.[^.]*$/, '')
		.toLowerCase()
		.replace(/[^a-z0-9._-]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 64);

/** Settings: CSV datasets that fields replay (`kind: replay`). */
export function DatasetsCard() {
	const datasets = useDatasets();
	const qc = useQueryClient();
	const [file, setFile] = useState<File>();
	const [name, setName] = useState('');
	const [errors, setErrors] = useState<Record<string, string>>({});
	const refresh = () => qc.invalidateQueries({ queryKey: keys.datasets });

	const create = useMutation({
		mutationFn: async () => unwrap(api.POST('/api/v1/datasets', { body: { name, csv: await file!.text() } })),
		onSuccess: (d) => {
			toast.success(`Uploaded ${d.name}: ${d.rows.toLocaleString()} rows`);
			setFile(undefined);
			setName('');
			setErrors({});
			refresh();
		},
		onError: (e) => {
			if (e instanceof ApiError) setErrors(e.fieldErrors());
			toast.error(e.message);
		},
	});
	const remove = useMutation({
		mutationFn: (n: string) => unwrap(api.DELETE('/api/v1/datasets/{name}', { params: { path: { name: n } } })),
		onSuccess: () => refresh(),
		onError: (e) => toast.error(e.message),
	});

	return (
		<Card title="Datasets" className="lg:col-span-2">
			<p className="mb-3 text-sm text-fg-muted">
				CSV recordings a field can replay (model “Replay”): a header row, then a time column (ISO date-times or Unix seconds) and value columns, separated by commas or semicolons.
				At most {MAX_BYTES / 1024 / 1024} MiB. A dataset cannot be edited: delete it and upload again.
			</p>
			<div className="mb-4 flex flex-wrap items-end gap-3">
				<label>
					<input
						type="file"
						accept=".csv,text/csv"
						className="hidden"
						onChange={(e) => {
							const f = e.target.files?.[0];
							setFile(f);
							if (f) setName((n) => n || nameOf(f.name));
							setErrors({});
						}}
					/>
					<span className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-border bg-surface px-3.5 text-sm font-medium hover:bg-muted">
						<Upload className="size-4" /> {file ? file.name : 'Choose CSV file…'}
					</span>
				</label>
				<Field label="Name" error={errors['/name']} className="w-64">
					<Input value={name} placeholder="office-temperature" onChange={(e) => setName(e.target.value)} />
				</Field>
				<Button variant="primary" disabled={!file || !name || create.isPending || (file?.size ?? 0) > MAX_BYTES} onClick={() => create.mutate()}>
					Upload
				</Button>
				{file && file.size > MAX_BYTES && <span className="text-xs text-danger">{kib(file.size)}: larger than {MAX_BYTES / 1024 / 1024} MiB</span>}
			</div>
			{errors['/csv'] && <p className="mb-3 rounded-md bg-danger-soft px-3 py-2 text-xs text-danger">The CSV {errors['/csv']}</p>}
			{!datasets.data ? (
				<Spinner />
			) : !datasets.data.length ? (
				<Empty title="No datasets yet" />
			) : (
				<table className="w-full text-sm">
					<thead className="text-left text-xs text-fg-muted">
						<tr>
							<th className="py-1.5 font-medium">Name</th>
							<th className="font-medium">Columns</th>
							<th className="font-medium">Rows</th>
							<th className="font-medium">Size</th>
							<th className="font-medium">Replayed by</th>
							<th className="font-medium">Uploaded</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{datasets.data.map((d) => (
							<tr key={d.name} className="border-t border-border align-top">
								<td className="py-2 font-mono">{d.name}</td>
								<td className="py-2 font-mono text-xs">{d.columns.join(', ')}</td>
								<td className="py-2 font-mono tabular-nums">{d.rows.toLocaleString()}</td>
								<td className="py-2 tabular-nums">{kib(d.sizeBytes)}</td>
								<td className="py-2 font-mono text-xs">{d.usedBy.join(', ') || <span className="text-fg-muted">—</span>}</td>
								<td className="py-2 text-fg-muted">{timeAgo(d.createdAt)}</td>
								<td className="py-2 text-right">
									<Button
										size="sm"
										variant="ghost"
										aria-label={`Delete ${d.name}`}
										disabled={d.usedBy.length > 0 || remove.isPending}
										title={d.usedBy.length ? 'Replayed by sensors: change them first' : 'Delete'}
										onClick={() => confirm(`Delete the dataset ${d.name}?`) && remove.mutate(d.name)}
									>
										<Trash2 className="size-3.5" />
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</Card>
	);
}
